/**
 * Judgment Analytics Page
 *
 * The leader-facing trust map: for each kind of call (judgment type) in each
 * domain, who is worth weighting — and why. Reads the workspace's decision
 * signals + outcomes and computes, on read, a profile per person × type ×
 * domain using the tested model layer (src/lib/judgment). It surfaces the
 * insight sweet spot (a good teammate with a proven, localized edge) rather
 * than raw contrarianism.
 *
 * Advisory only: nothing here weights, gates, or alters any ranking, score, or
 * authority state. Gated behind the judgment_analytics module + entitlement.
 */

import { useEffect, useMemo, useState } from 'react';
import { Compass, Info, Loader2, TrendingUp, Users } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  loadJudgmentAnalyticsSignals,
  getWorkspaceJudgmentInsights,
} from '../../../lib/decisionIntelligenceService';
import {
  buildCellProfiles,
  buildTrustMap,
  JUDGMENT_TYPES,
  SCORING_DIMENSIONS,
  type JudgmentCellProfile,
  type TrustMapEntry,
} from '../../../lib/judgment';
import type { InsightArchetype } from '../../../lib/judgment';

const ARCHETYPE_META: Record<
  InsightArchetype,
  { label: string; badge: string; blurb: string }
> = {
  insightful_teammate: {
    label: 'Insightful teammate',
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    blurb: 'Aligned most of the time, and right where they diverge. The sweet spot — seek their view.',
  },
  reliable_consensus: {
    label: 'Reliable consensus',
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    blurb: 'A steady, trustworthy read — no unique edge yet demonstrated.',
  },
  maverick: {
    label: 'Maverick',
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    blurb: 'Frequently divergent and often right, but low alignment. High value, harder to integrate — validate.',
  },
  contrarian_noise: {
    label: 'Contrarian',
    badge: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    blurb: 'Frequently divergent and not right. Down-weight.',
  },
  insufficient_data: {
    label: 'Insufficient data',
    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
    blurb: 'Too few resolved calls to judge yet.',
  },
};

const TIER_LABEL: Record<string, string> = {
  superforecaster: 'Superforecaster',
  strong: 'Strong',
  developing: 'Developing',
  insufficient_data: 'Building record',
};

function pct(value: number | null): string {
  return value == null ? '—' : `${Math.round(value * 100)}%`;
}

function edge(value: number | null): string {
  return value == null ? '—' : value.toFixed(2);
}

export function JudgmentAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trustMap, setTrustMap] = useState<TrustMapEntry[]>([]);
  const [emails, setEmails] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setError('No workspace selected.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [signals, insights] = await Promise.all([
          loadJudgmentAnalyticsSignals(workspaceId),
          getWorkspaceJudgmentInsights(workspaceId).catch(() => null),
        ]);
        if (cancelled) return;
        setTrustMap(buildTrustMap(buildCellProfiles(signals)));
        const map = new Map<string, string>();
        for (const p of insights?.profiles ?? []) if (p.email) map.set(p.userId, p.email);
        setEmails(map);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load judgment analytics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const label = (actorId: string, actorEmail?: string) =>
    actorEmail ?? emails.get(actorId) ?? `${actorId.slice(0, 8)}…`;

  const archetypeCounts = useMemo(() => {
    const counts = new Map<InsightArchetype, number>();
    const seen = new Set<string>();
    for (const cell of trustMap) {
      for (const p of cell.ranked) {
        const k = `${p.actorId}::${p.insightArchetype}`;
        if (seen.has(k)) continue;
        seen.add(k);
        counts.set(p.insightArchetype, (counts.get(p.insightArchetype) ?? 0) + 1);
      }
    }
    return counts;
  }, [trustMap]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <PageHeader
        icon={<Compass className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Judgment Analytics"
        subtitle="Whose judgment to seek, for which kind of call — from track record, not reputation."
      />

      <div className="mb-6 flex items-start gap-2 border border-[var(--rule)] bg-[var(--surface)] p-3 text-sm text-[var(--text-sec)]">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
        <p>
          Advisory only — this never alters rankings or authority. Scores are shrunk toward the
          group mean by sample size, so thin records are never over-ranked. The goal is the{' '}
          <span className="font-medium text-[var(--text-pri)]">insightful teammate</span>: aligned with
          consensus most of the time, and right where they diverge — not raw contrarianism.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[var(--text-ter)] py-16 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Computing judgment profiles…</span>
        </div>
      )}

      {error && !loading && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 text-sm text-[var(--coral)]">
          {error}
        </div>
      )}

      {!loading && !error && trustMap.length === 0 && (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-8 text-center">
          <Users className="h-6 w-6 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-[var(--text-sec)] font-medium">No resolved judgment yet</p>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Once decision signals are captured and their outcomes recorded, the trust map builds
            itself here — one cell per kind of call.
          </p>
        </div>
      )}

      {!loading && !error && trustMap.length > 0 && (
        <>
          {/* Archetype legend / distribution */}
          <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(Object.keys(ARCHETYPE_META) as InsightArchetype[])
              .filter((a) => a !== 'insufficient_data')
              .map((a) => (
                <div key={a} className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ARCHETYPE_META[a].badge}`}>
                      {ARCHETYPE_META[a].label}
                    </span>
                    <span className="text-lg font-semibold text-[var(--text-pri)]">
                      {archetypeCounts.get(a) ?? 0}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-ter)] mt-2 leading-snug">{ARCHETYPE_META[a].blurb}</p>
                </div>
              ))}
          </div>

          {/* Trust map, one card per type × domain cell */}
          <div className="space-y-6">
            {trustMap.map((cell) => (
              <div key={cell.cellKey} className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-[var(--text-pri)]">
                      {JUDGMENT_TYPES[cell.type]?.label ?? cell.type}
                      <span className="text-[var(--text-ter)] font-normal"> · {cell.domain}</span>
                    </h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Ranked by {SCORING_DIMENSIONS[cell.leadDimension as keyof typeof SCORING_DIMENSIONS]?.label ?? cell.leadDimension}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-[var(--text-ter)] border-b border-[var(--rule)]">
                        <th className="pb-2 font-medium">Person</th>
                        <th className="pb-2 font-medium">Read</th>
                        <th className="pb-2 font-medium text-right">Calibration</th>
                        <th className="pb-2 font-medium text-right">Edge vs crowd</th>
                        <th className="pb-2 font-medium text-right">Aligned</th>
                        <th className="pb-2 font-medium text-right">Resolved</th>
                        <th className="pb-2 font-medium text-right">Tier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cell.ranked.map((p: JudgmentCellProfile) => (
                        <tr key={p.actorId} className="border-b border-[var(--rule)] last:border-0">
                          <td className="py-2 text-[var(--text-pri)]">{label(p.actorId, p.actorEmail)}</td>
                          <td className="py-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${ARCHETYPE_META[p.insightArchetype].badge}`}>
                              {ARCHETYPE_META[p.insightArchetype].label}
                            </span>
                          </td>
                          <td className="py-2 text-right tabular-nums text-[var(--text-sec)]">{pct(p.calibration)}</td>
                          <td className="py-2 text-right tabular-nums text-[var(--text-sec)]">{edge(p.meanConsensusEdge)}</td>
                          <td className="py-2 text-right tabular-nums text-[var(--text-sec)]">{pct(p.baselineAlignment)}</td>
                          <td className="py-2 text-right tabular-nums text-[var(--text-ter)]">{p.resolvedCount}</td>
                          <td className="py-2 text-right text-[var(--text-sec)]">{TIER_LABEL[p.tier] ?? p.tier}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default JudgmentAnalyticsPage;
